import React, { useState, useEffect } from 'react';
import { Mic, MicOff, Headphones, VolumeX, PhoneOff, Monitor, Settings, Wifi, Video, VideoOff, Users, Volume2 } from 'lucide-react';
import './VoicePanel.css';

function VoicePanel({ channel, onDisconnect, settings }) {
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [connectionTime, setConnectionTime] = useState(0);
  const [participants, setParticipants] = useState([]);
  const [connectionQuality, setConnectionQuality] = useState('excellent');

  useEffect(() => {
    const mockParticipants = [
      { id: 1, name: settings.username, speaking: false, muted: isMuted, self: true, volume: 100 },
      ...(channel.users || []).map((user, idx) => ({
        id: idx + 2,
        name: user,
        speaking: Math.random() > 0.5,
        muted: false,
        self: false,
        volume: Math.floor(Math.random() * 30) + 70
      }))
    ];
    setParticipants(mockParticipants);
  }, [channel, settings.username, isMuted]);

  useEffect(() => {
    const timer = setInterval(() => {
      setConnectionTime(prev => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const qualityInterval = setInterval(() => {
      const qualities = ['excellent', 'good', 'excellent', 'excellent'];
      setConnectionQuality(qualities[Math.floor(Math.random() * qualities.length)]);
    }, 5000);
    return () => clearInterval(qualityInterval);
  }, []);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleMute = () => setIsMuted(!isMuted);
  const toggleDeafen = () => {
    setIsDeafened(!isDeafened);
    if (!isDeafened) setIsMuted(true);
  };
  const toggleVideo = () => setIsVideoOn(!isVideoOn);
  const toggleScreenShare = () => setIsScreenSharing(!isScreenSharing);

  return (
    <div className="voice-panel">
      <div className="voice-panel-inner">
        <div className="voice-panel-header">
          <div className="voice-info">
            <div className="voice-status">
              <div className={`connection-indicator ${connectionQuality}`}>
                <Wifi size={14} />
              </div>
              <span className="status-text">Voice Connected</span>
              <span className="quality-badge">{connectionQuality}</span>
            </div>
            <div className="voice-channel-info">
              <Volume2 size={14} />
              <span className="voice-channel-name">{channel.name}</span>
            </div>
          </div>
          <div className="voice-meta">
            <div className="participant-count">
              <Users size={14} />
              <span>{participants.length}</span>
            </div>
            <div className="connection-time">{formatTime(connectionTime)}</div>
          </div>
        </div>

        <div className="voice-participants">
          {participants.map((participant) => (
            <div 
              key={participant.id} 
              className={`participant ${participant.speaking ? 'speaking' : ''} ${participant.self ? 'self' : ''}`}
            >
              <div className="participant-avatar">
                <span>{participant.name[0]}</span>
                {participant.speaking && <div className="speaking-ring" />}
                {participant.speaking && <div className="speaking-glow" />}
              </div>
              <div className="participant-info">
                <span className="participant-name">{participant.name}</span>
                {participant.self && <span className="you-badge">You</span>}
              </div>
              <div className="participant-indicators">
                {participant.muted && <MicOff size={14} className="muted-icon" />}
                {participant.speaking && <div className="volume-bar"><div style={{width: `${participant.volume}%`}} /></div>}
              </div>
            </div>
          ))}
        </div>

        <div className="voice-controls">
          <div className="controls-left">
            <button 
              className={`voice-btn ${isMuted ? 'danger' : ''}`}
              onClick={toggleMute}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <MicOff size={20} /> : <Mic size={20} />}
              <span className="btn-label">{isMuted ? 'Unmute' : 'Mute'}</span>
            </button>
            <button 
              className={`voice-btn ${isDeafened ? 'danger' : ''}`}
              onClick={toggleDeafen}
              title={isDeafened ? 'Undeafen' : 'Deafen'}
            >
              {isDeafened ? <VolumeX size={20} /> : <Headphones size={20} />}
              <span className="btn-label">{isDeafened ? 'Undeafen' : 'Deafen'}</span>
            </button>
            <button 
              className={`voice-btn ${isVideoOn ? 'active' : ''}`}
              onClick={toggleVideo}
              title={isVideoOn ? 'Turn Off Video' : 'Turn On Video'}
            >
              {isVideoOn ? <Video size={20} /> : <VideoOff size={20} />}
              <span className="btn-label">Video</span>
            </button>
            <button 
              className={`voice-btn ${isScreenSharing ? 'active' : ''}`}
              onClick={toggleScreenShare}
              title={isScreenSharing ? 'Stop Sharing' : 'Share Screen'}
            >
              <Monitor size={20} />
              <span className="btn-label">Share</span>
            </button>
          </div>
          <div className="controls-right">
            <button className="voice-btn settings-btn" title="Voice Settings">
              <Settings size={18} />
            </button>
            <button 
              className="voice-btn disconnect"
              onClick={onDisconnect}
              title="Disconnect"
            >
              <PhoneOff size={20} />
              <span className="btn-label">Leave</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VoicePanel;
